import { Subscription } from 'rxjs';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';

import { NgIconComponent } from '@ng-icons/core';

import { CapsLockComponent } from '@components/caps-lock/caps-lock.component';
import { ErrorSmallComponent } from '@components/error-handling/error-small/error-small.component';

import { KEYBOARD_KEY } from '@cxc/core/enums';

import { ErrorStore, UserStore } from '@cxc/core/services';
import { IdentityAuthAPI } from '@services/API/identity/auth.service';
import { FormService } from '@services/form.service';
import { NavigationService } from '@services/navigation.service';
import { AppConfigStore } from '@services/store/app-config.store';

import { CustomValidators } from '@cxc/core/utils';

@Component({
  selector: 'login-page',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,

    ButtonModule,
    InputTextModule,
    CheckboxModule,
    RippleModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    PasswordModule,
    DividerModule,

    ErrorSmallComponent,
    CapsLockComponent,

    NgIconComponent,
  ],
})
export class LoginPage implements OnInit {
  formService = inject(FormService);
  errorStore = inject(ErrorStore);

  loginForm!: FormGroup;
  sub!: Subscription;
  checkbox = signal(false);

  constructor(
    public router: Router,
    public navigationService: NavigationService,
    fb: FormBuilder,

    private identityAuthAPI: IdentityAuthAPI,
    private userStore: UserStore,
    private appConfigStore: AppConfigStore
  ) {
    this.loginForm = fb.group({
      email: ['', [CustomValidators.required(), CustomValidators.email()]],
      password: [
        '',
        [CustomValidators.required(), ...CustomValidators.password('password')],
      ],
    });
  }

  ngOnInit(): void {
    this.errorStore.clear();

    this.checkbox.set(this.userStore.get().rememberMe!);

    if (this.checkbox()) {
      this.loginForm.controls['email'].setValue(this.userStore.get().email);
    }
  }

  logIn(keyEvent?: KeyboardEvent) {
    if (keyEvent && keyEvent.key !== KEYBOARD_KEY.Enter) {
      return;
    }

    if (this.formService.trySubmitForm(this.loginForm)) {
      return;
    }

    this.sub = this.identityAuthAPI
      .login({
        email: this.loginForm.value['email'],
        password: this.loginForm.value['password'],
      })
      .subscribe({
        next: response => {
          this.userStore.patch({
            email: this.loginForm.value['email'],
            session: response.session,
            challenge_name: response.challenge_name,
          });

          this.appConfigStore.patch({ allowRoutes: { '2FA': true } });

          this.router.navigateByUrl('/2FA');
        },
      });
  }

  toggleCheckBox() {
    this.checkbox.update(c => {
      this.onCheckBoxChange({ checked: !c });
      return !c;
    });
  }

  onCheckBoxChange(event: CheckboxChangeEvent) {
    this.userStore.patch({ rememberMe: event.checked });
  }
}
